// ProductForm.js
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Switch from 'components/_UI/Switch';
import ImageProduct from './parts/ImageProduct';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fieldValidations from 'helpers/fieldValidations';
import imageUploadApi from 'api/requests/imageUpload';
import productsApi from 'api/requests/products';
import { HorizontalCenter } from 'styled/alignment/Center';
import { useUser } from 'context/user/User';
import ticketsApi from 'api/requests/tickets';
import { Store } from 'lucide-react';
import { panelAccent } from 'theme/panelAccent';

export default function ProductForm({
  selectedProduct,
  activeTab,
  onClose = () => {},
  onSuccess = () => {},
  onError = () => {},
}) {
  const { user } = useUser()
  const [isLoading, setIsLoading] = useState(false);
  const [resetState, setResetState] = useState(false);
  const [preUploadedImage, setPreUploadedImage] = useState(null);
  const [currentFormConfig, setCurrentFormConfig] = useState({})
  const [isImageMarkedForDeletion, setIsImageMarkedForDeletion] = useState(false);
  const [sellAtEstablishment, setSellAtEstablishment] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      productName: '',
      description: '',
      quantity: '',
      price: '',
      is_active: false,
    }
  });

  const preUploadImage = (file) => {
    setPreUploadedImage(file)
    setIsImageMarkedForDeletion(false)
  }

  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', 'products');

    const { data } = await imageUploadApi.uploadWithFolder(formData);
    
    return data
  }

  const markImageForDeletion = () => {
    setIsImageMarkedForDeletion(true)
    setPreUploadedImage(null)
  }

  const onResetForm = () => {
    setPreUploadedImage(null)
    setIsImageMarkedForDeletion(false)
    reset({
      productName: '',
      description: '',
      quantity: '',
      price: '',
      is_active: false
    });
    setResetState(prev => !prev); 
  };

  const handleCurrencyChange = (value) => {
    if (value) {
        const formattedValue = value.replace('.', ''); 
        return formattedValue;
    }
    return '';
  };

  const createBaseProductPayload = (content) => {
    console.log(content)
    const productName = content.productName;
    const parsedPrice = parseFloat(content.price.replace(',', '.')).toFixed(2);
    const parsedQuantity = parseInt(content.quantity);

    const payload = {
      name: productName,
      price: parsedPrice,
      type: 'product',
      quantity: parsedQuantity,
      description: content.description,
      image_id: content?.image?.id,
      image: content?.image?.path
    };

    if (!payload.description) {
      delete payload.description;
    }

    if (!payload.image_id) {
      delete payload.image_id;
      delete payload.image;
    }

    if (content?.batches) {
      payload.batches = [
        {
          ...content?.batches[0],
          is_active: content.is_active,
          name: productName,
          price: parsedPrice,
          quantity: parsedQuantity,
        }
      ];

      delete payload.batches[0]?.quantity_available;
    }

    return payload;
  };

  // Cria ou atualiza o Product vinculado ao estabelecimento a partir de um
  // modelo — mesma chamada que a aba "A venda no estabelecimento" já usa,
  // só que carregando product_template_id pra manter o vínculo de volta ao
  // modelo (sem isso, reabrir o modelo depois não sabe que ele já está
  // publicado, e cada edição cria uma linha nova e duplicada).
  const syncEstablishmentProduct = async (templateId, content, existingLinkId) => {
    if (!user || !user.establishments || !user.establishments[0] || !user.establishments[0].id) {
      return
    }

    const payload = {
      ...createBaseProductPayload(content),
      establishment_id: user.establishments[0].id,
      sale_type: 'batch',
      product_template_id: templateId,
    }

    if (existingLinkId) {
      await ticketsApi.update(existingLinkId, payload)
    } else {
      await ticketsApi.create(payload)
    }
  }

  const createProduct = async (content) => {
    try {
      setIsLoading(true);

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      const payload = createBaseProductPayload(content);

      const { data } = await productsApi.create(payload);

      if (sellAtEstablishment) {
        await syncEstablishmentProduct(data.id, content);
      }

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      onError();
    } finally {
      onClose();
    }
  };

  const editProduct = async (content) => {
    try {
      setIsLoading(true);

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      if (isImageMarkedForDeletion && selectedProduct?.image_id) {
        await imageUploadApi.delete(selectedProduct.image_id)
      }

      const payload = createBaseProductPayload(content);
      await productsApi.update(selectedProduct?.id, payload);

      const existingLinkId = selectedProduct?.establishment_product?.id;
      if (sellAtEstablishment) {
        await syncEstablishmentProduct(selectedProduct.id, content, existingLinkId);
      } else if (existingLinkId) {
        // Toggle desligado num modelo que já estava publicado — remove a
        // publicação em vez de deixar um item à venda "fantasma".
        await ticketsApi.delete(existingLinkId);
      }

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      onError();
    } finally {
      onClose();
    }
  };

  const editSellableProduct = async (content) => {
    try {
      setIsLoading(true)

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      if (isImageMarkedForDeletion && selectedProduct?.image_id) {
        await imageUploadApi.delete(selectedProduct.image_id)
      }

      const payload = createBaseProductPayload(content)
  
      await ticketsApi.update(selectedProduct.id, payload)

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      console.error(error)
      onError(error)
    } finally {
      onClose()
    }
  }

  // Produto avulso do estabelecimento, sem nenhum modelo por trás — a aba
  // "A venda no estabelecimento" continua existindo pra esse caso.
  const sellEstablishmentProduct = async (content) => {
    if (!user || !user.establishments || !user.establishments[0] || !user.establishments[0].id) {
      return
    }

    try {
      setIsLoading(true)

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      const payload = {
        ...createBaseProductPayload(content),
        establishment_id: user.establishments[0].id,
        sale_type: 'batch'
      }

      await ticketsApi.create(payload)

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      console.error(error)
      onError(error)
    } finally {
      onClose()
    }
  }

  const onSubmit = async (formData) => {
    if (selectedProduct) {
      const { action } = selectedProduct;

      // "Editar" e o atalho "Vender no perfil" (a partir de um modelo) caem
      // no mesmo lugar: salvar o modelo e, se o toggle estiver ligado,
      // sincronizar o produto do estabelecimento — o que muda entre os dois
      // é só o valor inicial do toggle, decidido no useEffect abaixo.
      if (activeTab === 'template' && (action === 'edit' || action === 'sell_establishment')) {
        await editProduct(formData);
        return;
      }

      if (activeTab === 'establishment' && action === 'edit') {
        await editSellableProduct({
          batches: selectedProduct?.batches,
          ...formData
        });
        return;
      }
    }

    if (!selectedProduct) {
      if (activeTab === 'establishment') {
        await sellEstablishmentProduct(formData);
        return;
      }

      // "A venda em eventos" não tem formulário de criação próprio (exigiria
      // escolher o evento, isso já é feito no assistente de evento) — o
      // botão global "Adicionar produto" cai aqui como rede de segurança em
      // vez de quebrar, e cria um produto comum do catálogo.
      if (activeTab === 'template' || activeTab === 'event') {
        await createProduct(formData);
      }
    }
  };

  const formConfig = {
    template: {
      create: {
        formTitle: 'Criar produto',
        submitButtonLabel: 'Criar produto',
      },
      edit: {
        formTitle: 'Editar produto',
        submitButtonLabel: 'Editar produto',
      },
      sell_establishment: {
        formTitle: 'Vender produto no estabelecimento',
        submitButtonLabel: 'Vender produto',
      },
    },
    establishment: {
      create: {
        formTitle: 'Vender produto no estabelecimento',
        submitButtonLabel: 'Vender produto',
      },
      edit: {
        formTitle: 'Editar produto a venda',
        submitButtonLabel: 'Editar produto',
      },
    },
    event: {
      // Reaproveita o "criar" do catálogo de propósito: essa aba não tem
      // formulário de criação próprio (veja o comentário em onSubmit) — e
      // não tem "editar" aqui porque "editar" nessa aba navega pro
      // assistente do evento em vez de abrir este formulário.
      create: {
        formTitle: 'Criar produto',
        submitButtonLabel: 'Criar produto',
      },
    },
  }

  useEffect(() => {
    if (selectedProduct) {
      reset({
        productName: selectedProduct.name,
        description: selectedProduct.description || '',
        quantity: selectedProduct.quantity,
        price: selectedProduct.price,
        is_active: selectedProduct?.batches?.[0]?.is_active !== undefined ? Boolean(selectedProduct?.batches?.[0]?.is_active) : null,
      });
      setCurrentFormConfig(formConfig[activeTab][selectedProduct.action])
      setPreUploadedImage(null)
      setIsImageMarkedForDeletion(false)

      if (activeTab === 'template') {
        // O atalho "Vender no perfil" já chega com o toggle ligado; editar um
        // modelo comum reflete se ele já está publicado no estabelecimento.
        setSellAtEstablishment(
          selectedProduct.action === 'sell_establishment'
            ? true
            : Boolean(selectedProduct?.establishment_product)
        );
      }
    }

    if (!selectedProduct) {
      onResetForm();
      setCurrentFormConfig(formConfig[activeTab].create)
      setSellAtEstablishment(false);
    }
  }, [selectedProduct]);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Text name='title' marginBottom={'20px'} color={panelAccent(colors.occasionalPurple)}>
        {currentFormConfig.formTitle}
      </Text>

      <ContentInputs>
        <Column verticalCenter>
          <Fieldset
            layout='squired'
            label='Nome do produto'
            name="productName"
            validations={fieldValidations.generic('nome', 2, 100)}
            register={register}
          />

          {errors.productName && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.productName.message}</Text>
            </HorizontalCenter>
          )}

          <Fieldset
            layout='squired'
            label='Descrição do produto'
            name='description'
            validations={fieldValidations.description('descrição', 200)}
            register={register}
          />
          {errors.description && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.description.message}</Text>
            </HorizontalCenter>
          )}

          <Fieldset
            label='Quantidade'
            layout='squired'
            name='quantity'
            validations={fieldValidations.number('quantidade')}
            register={register}
          />

          {errors.quantity && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.quantity.message}</Text>
            </HorizontalCenter>
          )}

          <Controller
            name='price'
            control={control}
            rules={{ required: 'O preço é obrigatório' }}
            render={({ field: { onChange, value, ref } }) => (
                <CurrencyInputField
                    label='Preço sugerido'
                    value={value}
                    layout='squired'
                    onChange={(value) => {
                        const formattedValue = handleCurrencyChange(value);
                        onChange(formattedValue); // Passa o valor formatado para onChange
                    }}
                    ref={ref}
                />
            )}
          />

          {errors.price && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.price.message}</Text>
            </HorizontalCenter>
          )}

          {
            getValues('is_active') !== null &&
              <Column>
                <Text
                  name='small'
                  type='bold'
                  color={colors.nightDark}
                  paddingBottom={'5px'}
                >
                  Produto ativo
                </Text>
                <Controller
                  name='is_active'
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Column>
          }

          {activeTab === 'template' && (
            <EstablishmentToggleCard $active={sellAtEstablishment}>
              <EstablishmentToggleIcon $active={sellAtEstablishment}>
                <Store size={18} />
              </EstablishmentToggleIcon>
              <EstablishmentToggleText>
                <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                  Vender também no meu estabelecimento
                </Text>
                <Text name='mini' color={colors.night} paddingBottom={0} marginTop={'3px'}>
                  {user?.establishments?.length
                    ? 'Publica este produto na página do seu estabelecimento, pronto para compra.'
                    : 'Você precisa ter um estabelecimento pra usar isso.'}
                </Text>
              </EstablishmentToggleText>
              <SwitchSlot>
                <Switch
                  checked={sellAtEstablishment}
                  onChange={setSellAtEstablishment}
                  disabled={!user?.establishments?.length}
                />
              </SwitchSlot>
            </EstablishmentToggleCard>
          )}
        </Column>

        <Column verticalCenter>
          <ImageProduct
            product
            selectedProduct={selectedProduct}
            onToggleImage={preUploadImage}
            onDeleteFile={markImageForDeletion}
            resetState={resetState}
          />
        </Column>
      </ContentInputs>

      {!isLoading ? (
        <ContentButton>
          <Button marginTop={'20px'} type="submit" variant="featured">
            {currentFormConfig.submitButtonLabel}
          </Button>
        </ContentButton>
      ) : (
        <Loader />
      )}
    </StyledForm>
  );
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentInputs = styled(Column)`
  align-items: center;
  @media screen and (min-width: ${breakpoints.sm}) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const ContentButton = styled.div`
  width: 100%;
`;

const EstablishmentToggleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${({ $active }) => $active ? `${colors.goldLight}55` : colors.neutralDark};
  background: ${({ $active }) => $active ? `${colors.goldLight}0F` : colors.neutral};
  transition: border-color 0.2s ease, background 0.2s ease;
`;

const EstablishmentToggleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  flex-shrink: 0;
  color: ${({ $active }) => $active ? colors.goldDark : colors.night};
  background: ${({ $active }) => $active ? `${colors.goldLight}22` : colors.neutralDark};
  transition: color 0.2s ease, background 0.2s ease;
`;

const EstablishmentToggleText = styled.div`
  flex: 1;
  min-width: 0;
`;

// Switch's own root is a Row with width: 100% (fine when it's alone on its
// line elsewhere in the app) — as a third flex sibling here that percentage
// resolves against the whole card, so it fights EstablishmentToggleText for
// space instead of just sitting at its own ~30px. Boxing it in a fixed-width,
// non-shrinking slot contains that 100% to the slot itself.
const SwitchSlot = styled.div`
  flex-shrink: 0;
  width: 38px;
`;
